import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Store, Package2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getShopUrl } from '../../lib/domains';

const EMPTY_STORE = {
  store_name: '',
  username: '',
  logo_url: '',
  cover_image_url: '',
  short_description: '',
  whatsapp_number: '',
  email: '',
  phone: '',
  location: '',
  business_category: '',
  currency: 'UGX',
  status: 'draft',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  social_sharing_image_url: '',
  remove_branding: false,
  delivery_enabled: true,
  pickup_enabled: true,
  delivery_fee: '',
  pickup_location: '',
  delivery_notes: '',
  request_marketplace_listing: false,
  marketplace_category: '',
  marketplace_description: '',
  marketplace_location: '',
  listed_in_marketplace: false,
  marketplace_request_status: 'none',
  featured_until: '',
};

const EMPTY_PRODUCT = {
  name: '',
  slug: '',
  images: '',
  short_description: '',
  full_description: '',
  price: '',
  sale_price: '',
  currency: 'UGX',
  category: '',
  stock_status: 'in_stock',
  sku: '',
  variants: '',
  status: 'draft',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
};

export default function StorePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeForm, setStoreForm] = useState(EMPTY_STORE);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingStore, setSavingStore] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const storeLogoInputRef = useRef(null);
  const storeCoverInputRef = useRef(null);
  const productImageInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/stores');
      setData(r.data);
      const store = r.data.stores?.[0];
      if (store) {
        setStoreForm({
          store_name: store.store_name || '',
          username: store.username || '',
          logo_url: store.logo_url || '',
          cover_image_url: store.cover_image_url || '',
          short_description: store.short_description || '',
          whatsapp_number: store.whatsapp_number || '',
          email: store.email || '',
          phone: store.phone || '',
          location: store.location || '',
          business_category: store.business_category || '',
          currency: store.currency || 'UGX',
          status: store.status || 'draft',
          seo_title: store.seo_title || '',
          seo_description: store.seo_description || '',
          seo_keywords: Array.isArray(store.seo_keywords) ? store.seo_keywords.join(', ') : (store.seo_keywords || ''),
          social_sharing_image_url: store.social_sharing_image_url || '',
          remove_branding: !!store.remove_branding,
          delivery_enabled: store.delivery_enabled ?? true,
          pickup_enabled: store.pickup_enabled ?? true,
          delivery_fee: store.delivery_fee ?? '',
          pickup_location: store.pickup_location || '',
          delivery_notes: store.delivery_notes || '',
          request_marketplace_listing: false,
          marketplace_category: store.marketplace_category || '',
          marketplace_description: store.marketplace_description || '',
          marketplace_location: store.marketplace_location || '',
          listed_in_marketplace: !!store.listed_in_marketplace,
          marketplace_request_status: store.marketplace_request_status || 'none',
          featured_until: store.featured_until || '',
        });
        const productsRes = await api.get(`/dashboard/stores/${store.id}/products`);
        setData(prev => ({ ...(prev || {}), ...productsRes.data, categories: productsRes.data.categories || [] }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load store settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const store = data?.stores?.[0] || data?.store || null;
  const products = data?.products || [];
  const categories = data?.categories || [];
  const limit = data?.limit || data?.access?.product_limit || 0;
  const remaining = limit > 0 ? Math.max(0, limit - products.length) : null;
  const marketplaceAccess = !!data?.access?.can_use_marketplace;

  const uploadStoreMedia = async (file, kind) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    if (store?.id) fd.append('store_id', store.id);
    const res = await api.post('/dashboard/stores/media', fd);
    return res.data?.url || '';
  };

  const saveStore = async (e) => {
    e.preventDefault();
    setSavingStore(true);
    try {
      const payload = { ...storeForm };
      if (!data?.access?.can_use_marketplace) {
        delete payload.request_marketplace_listing;
        delete payload.marketplace_category;
        delete payload.marketplace_description;
        delete payload.marketplace_location;
        delete payload.listed_in_marketplace;
        delete payload.marketplace_request_status;
        delete payload.featured_until;
      }
      payload.seo_keywords = String(payload.seo_keywords || '').split(',').map(v => v.trim()).filter(Boolean);
      const res = store?.id
        ? await api.put(`/dashboard/stores/${store.id}`, payload)
        : await api.post('/dashboard/stores', payload);
      setData(prev => ({ ...(prev || {}), stores: [res.data] }));
      toast.success('Store saved');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save store');
    } finally {
      setSavingStore(false);
    }
  };

  const handleStoreMediaPick = async (file, field) => {
    try {
      const url = await uploadStoreMedia(file, field);
      if (!url) return;
      setStoreForm(prev => ({ ...prev, [field === 'store_logo' ? 'logo_url' : 'cover_image_url']: url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleProductImagePick = async (file) => {
    try {
      const url = await uploadStoreMedia(file, 'product_image');
      if (!url) return;
      setProductForm(prev => ({
        ...prev,
        images: prev.images ? `${prev.images}, ${url}` : url,
      }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!store?.id) return toast.error('Create your store first');
    setSavingProduct(true);
    try {
      const payload = { ...productForm };
      payload.seo_keywords = String(payload.seo_keywords || '').split(',').map(v => v.trim()).filter(Boolean);
      payload.images = String(payload.images || '').split(',').map(v => v.trim()).filter(Boolean);
      payload.variants = String(payload.variants || '').split(',').map(v => v.trim()).filter(Boolean);
      const res = editingProduct
        ? await api.put(`/dashboard/products/${editingProduct.id}`, payload)
        : await api.post(`/dashboard/stores/${store.id}/products`, payload);
      toast.success(editingProduct ? 'Product updated' : 'Product added');
      setEditingProduct(null);
      setProductForm(EMPTY_PRODUCT);
      await load();
      if (res?.data?.id) {
        // no-op, refresh already handled
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSavingProduct(false);
    }
  };

  const editProduct = (prod) => {
    setEditingProduct(prod);
    setProductForm({
      name: prod.name || '',
      slug: prod.slug || '',
      images: Array.isArray(prod.images) ? prod.images.join(', ') : (prod.images || ''),
      short_description: prod.short_description || '',
      full_description: prod.full_description || '',
      price: prod.price || '',
      sale_price: prod.sale_price || '',
      currency: prod.currency || 'UGX',
      category: prod.category || '',
      stock_status: prod.stock_status || 'in_stock',
      sku: prod.sku || '',
      variants: Array.isArray(prod.variants) ? prod.variants.join(', ') : (prod.variants || ''),
      status: prod.status || 'draft',
      seo_title: prod.seo_title || '',
      seo_description: prod.seo_description || '',
      seo_keywords: Array.isArray(prod.seo_keywords) ? prod.seo_keywords.join(', ') : (prod.seo_keywords || ''),
    });
  };

  const deleteProduct = async (prod) => {
    try {
      await api.delete(`/dashboard/products/${prod.id}`);
      toast.success('Product deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>My Store</h1>
        <p className="text-slate-500 text-sm mt-0.5">Create a mobile-first store and manage your products.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => window.open(getShopUrl('/directory'), '_blank', 'noopener,noreferrer')}>
          Marketplace Directory
        </Button>
      </div>

      {data?.access && !data.access.can_create_store && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>Your package does not currently allow store creation. Upgrade your package to unlock this feature.</span>
        </div>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Store size={18} /> Store Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveStore} className="grid gap-4 md:grid-cols-2">
            {[
              ['Store Name', 'store_name'],
              ['Store Username', 'username'],
              ['Logo URL', 'logo_url'],
              ['Cover Image URL', 'cover_image_url'],
              ['WhatsApp Number', 'whatsapp_number'],
              ['Email', 'email'],
              ['Phone', 'phone'],
              ['Location', 'location'],
              ['Business Category', 'business_category'],
              ['Currency', 'currency'],
              ['SEO Title', 'seo_title'],
              ['Social Sharing Image URL', 'social_sharing_image_url'],
            ].map(([label, field]) => (
              <div key={field}>
                <Label>{label}</Label>
                <Input
                  value={storeForm[field]}
                  onChange={(e) => setStoreForm(prev => ({ ...prev, [field]: e.target.value }))}
                  className="mt-1"
                />
              </div>
            ))}
            <div className="md:col-span-2 flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => storeLogoInputRef.current?.click()}>Upload Logo</Button>
              <Button type="button" variant="outline" onClick={() => storeCoverInputRef.current?.click()}>Upload Cover</Button>
              <input ref={storeLogoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleStoreMediaPick(e.target.files?.[0], 'store_logo')} />
              <input ref={storeCoverInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleStoreMediaPick(e.target.files?.[0], 'store_cover')} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={storeForm.status} onValueChange={(v) => setStoreForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Short Description</Label>
              <Textarea value={storeForm.short_description} onChange={(e) => setStoreForm(prev => ({ ...prev, short_description: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>SEO Description</Label>
              <Textarea value={storeForm.seo_description} onChange={(e) => setStoreForm(prev => ({ ...prev, seo_description: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>SEO Keywords</Label>
              <Input value={storeForm.seo_keywords} onChange={(e) => setStoreForm(prev => ({ ...prev, seo_keywords: e.target.value }))} className="mt-1" placeholder="fashion, shop, uganda" />
            </div>
            <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm text-slate-600 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <input type="checkbox" checked={!!storeForm.delivery_enabled} onChange={e => setStoreForm(prev => ({ ...prev, delivery_enabled: e.target.checked }))} />
                Enable delivery
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <input type="checkbox" checked={!!storeForm.pickup_enabled} onChange={e => setStoreForm(prev => ({ ...prev, pickup_enabled: e.target.checked }))} />
                Enable pickup
              </label>
            </div>
            <div>
              <Label>Delivery Fee</Label>
              <Input type="number" min="0" step="0.01" value={storeForm.delivery_fee} onChange={(e) => setStoreForm(prev => ({ ...prev, delivery_fee: e.target.value }))} className="mt-1" placeholder="0" />
            </div>
            <div>
              <Label>Pickup Location</Label>
              <Input value={storeForm.pickup_location} onChange={(e) => setStoreForm(prev => ({ ...prev, pickup_location: e.target.value }))} className="mt-1" placeholder="Store pickup location" />
            </div>
            <div className="md:col-span-2">
              <Label>Delivery Notes</Label>
              <Textarea value={storeForm.delivery_notes} onChange={(e) => setStoreForm(prev => ({ ...prev, delivery_notes: e.target.value }))} className="mt-1" rows={3} placeholder="Extra delivery instructions or pickup instructions" />
            </div>
            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Marketplace listing</h3>
                  <p className="text-xs text-slate-500">Request approval to appear in the public store directory.</p>
                </div>
                <div className="text-right text-xs">
                  <p className={storeForm.listed_in_marketplace ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
                    {storeForm.listed_in_marketplace ? 'Listed in marketplace' : (storeForm.marketplace_request_status === 'pending' ? 'Listing request pending' : 'Not listed')}
                  </p>
                  {storeForm.featured_until && <p className="text-slate-400">Featured until: {new Date(storeForm.featured_until).toLocaleDateString()}</p>}
                </div>
              </div>
              {data?.access && !data.access.can_use_marketplace && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  Your package does not allow marketplace listings. Upgrade to request a listing.
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={!!storeForm.request_marketplace_listing}
                  disabled={!marketplaceAccess}
                  onChange={e => setStoreForm(prev => ({ ...prev, request_marketplace_listing: e.target.checked }))}
                />
                Request marketplace listing
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Marketplace Category</Label>
                  <Input
                    value={storeForm.marketplace_category}
                    disabled={!marketplaceAccess}
                    onChange={(e) => setStoreForm(prev => ({ ...prev, marketplace_category: e.target.value }))}
                    className="mt-1"
                    placeholder="Fashion, Electronics, Food..."
                  />
                </div>
                <div>
                  <Label>Marketplace Location</Label>
                  <Input
                    value={storeForm.marketplace_location}
                    disabled={!marketplaceAccess}
                    onChange={(e) => setStoreForm(prev => ({ ...prev, marketplace_location: e.target.value }))}
                    className="mt-1"
                    placeholder="Kampala, Jinja, Mbarara..."
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Marketplace Description</Label>
                  <Textarea
                    value={storeForm.marketplace_description}
                    disabled={!marketplaceAccess}
                    onChange={(e) => setStoreForm(prev => ({ ...prev, marketplace_description: e.target.value }))}
                    className="mt-1"
                    rows={3}
                    placeholder="Short description for the public directory"
                  />
                </div>
              </div>
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={!!storeForm.remove_branding} onChange={e => setStoreForm(prev => ({ ...prev, remove_branding: e.target.checked }))} />
              Remove Jjuma branding if your package allows it
            </label>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={savingStore}>
                <Plus size={16} className="mr-1" /> {savingStore ? 'Saving...' : (store ? 'Update Store' : 'Create Store')}
              </Button>
              {store?.username && (
                <p className="text-sm text-slate-500">Public URL: <span className="font-mono text-slate-900">shop.jjuma.com/{store.username}</span></p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Package2 size={18} /> Products</CardTitle>
          <p className="text-xs text-slate-500">{products.length}{limit ? ` / ${limit}` : ''} products</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {remaining === 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              You have reached your product limit. Upgrade your package to add more products.
            </div>
          )}

          <form onSubmit={saveProduct} className="grid gap-4 md:grid-cols-2">
            {[
              ['Product Name', 'name'],
              ['Slug', 'slug'],
              ['Images (comma separated URLs)', 'images'],
              ['Price', 'price'],
              ['Sale Price', 'sale_price'],
              ['Currency', 'currency'],
              ['Category', 'category'],
              ['SKU', 'sku'],
              ['Variants (comma separated)', 'variants'],
              ['SEO Title', 'seo_title'],
            ].map(([label, field]) => (
              <div key={field}>
                <Label>{label}</Label>
                <Input
                  value={productForm[field]}
                  onChange={(e) => setProductForm(prev => ({ ...prev, [field]: e.target.value }))}
                  className="mt-1"
                />
              </div>
            ))}
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => productImageInputRef.current?.click()}>Upload Product Image</Button>
              <input ref={productImageInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleProductImagePick(e.target.files?.[0])} />
              <p className="text-xs text-slate-500">Uploads append the image URL to the product images field.</p>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={productForm.status} onValueChange={(v) => setProductForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stock Status</Label>
              <Select value={productForm.stock_status} onValueChange={(v) => setProductForm(prev => ({ ...prev, stock_status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_stock">In stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Short Description</Label>
              <Textarea value={productForm.short_description} onChange={(e) => setProductForm(prev => ({ ...prev, short_description: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>Full Description</Label>
              <Textarea value={productForm.full_description} onChange={(e) => setProductForm(prev => ({ ...prev, full_description: e.target.value }))} className="mt-1" rows={4} />
            </div>
            <div className="md:col-span-2">
              <Label>SEO Description</Label>
              <Textarea value={productForm.seo_description} onChange={(e) => setProductForm(prev => ({ ...prev, seo_description: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2">
              <Label>SEO Keywords</Label>
              <Input value={productForm.seo_keywords} onChange={(e) => setProductForm(prev => ({ ...prev, seo_keywords: e.target.value }))} className="mt-1" placeholder="shoes, fashion, uganda" />
            </div>
            <div className="md:col-span-2">
              <Label>Category Suggestions</Label>
              <Input list="store-categories" value={productForm.category} onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))} className="mt-1" placeholder="Use an existing category or create a new one" />
              <datalist id="store-categories">
                {categories.map((category) => (
                  <option key={category.id} value={category.name} />
                ))}
              </datalist>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={savingProduct || (remaining === 0 && !editingProduct)}>
                <Plus size={16} className="mr-1" /> {savingProduct ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
              </Button>
              {editingProduct && (
                <Button type="button" variant="outline" onClick={() => { setEditingProduct(null); setProductForm(EMPTY_PRODUCT); }}>
                  Cancel
                </Button>
              )}
            </div>
          </form>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((prod) => (
                  <TableRow key={prod.id}>
                    <TableCell>{prod.name}</TableCell>
                    <TableCell className="font-mono text-xs">{prod.slug}</TableCell>
                    <TableCell>{prod.currency} {Number(prod.price || 0).toLocaleString()}</TableCell>
                    <TableCell>{prod.status}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => editProduct(prod)}>
                          <Pencil size={14} className="mr-1" /> Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => deleteProduct(prod)} className="text-red-600">
                          <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">No products yet.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
